import axios from "axios";
import routeConstant from "./utils/routeConstant";
import { ToastTypes, notify } from "./utils/helpers";

const getToken = () => {
  return localStorage.getItem("AccessToken");
};

// 🟢 Keep track of all pending requests
const pendingRequests = new Map();

const getRequestKey = (config: any) => {
  return `${config.method}-${config.url}-${JSON.stringify(config.data || {})}`;
};

const removePendingRequest = (config: any) => {
  const requestKey = getRequestKey(config);
  if (pendingRequests.has(requestKey)) {
    const cancelToken = pendingRequests.get(requestKey);
    cancelToken(); // cancel previous duplicate
    pendingRequests.delete(requestKey);
  }
};

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: getToken(),
  },
});

// 🔹 Request Interceptor
axiosClient.interceptors.request.use(
  function (config) {
    // cancel duplicate before sending
    removePendingRequest(config);

    const requestKey = getRequestKey(config);
    config.cancelToken = new axios.CancelToken((cancel) => {
      pendingRequests.set(requestKey, cancel);
    });

    // attach token
    const token = getToken();
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// 🔹 Response Interceptor
axiosClient.interceptors.response.use(
  function (response) {
    removePendingRequest(response.config);
    return response;
  },
  function (error) {
    if (error.config) {
      removePendingRequest(error.config);
    }

    let res = error.response;
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const authUrls = [
      `${apiBaseUrl}/auth/login`,
      `${apiBaseUrl}/auth/social/login`,
      `${apiBaseUrl}/auth/signup`,
      `${apiBaseUrl}/auth/forgotPassword`,
    ];
    const isLoginUrl = authUrls.includes(error.request?.responseURL);

    if (res?.status === 401) {
      localStorage.clear();
      window.location.replace(`/${routeConstant.login}`);
    }

    if (
      error?.code !== "ERR_CANCELED" &&
      !isLoginUrl &&
      !window.location.pathname.includes("myFolders")
    ) {
      notify({ message: res?.data?.message, type: ToastTypes.ERROR });
    }

    if (res?.status === 402) {
      setTimeout(() => {
        window.location.replace(`${routeConstant.subscription}`);
      }, 2000);
    }

    console.error("Looks like there was a problem. Status Code: " + res);
    return Promise.reject(error);
  }
);

export default axiosClient;
